import type { CUID } from "./CUID"
import type { RatingAnomaly } from "./Rating"
import type { TranslatedText } from "./TranslatedText"
import type { BusinessArea } from "./enums"

/**
 * Separator used in convention-based keyed datapoint names.
 * Chosen because `_` is a valid MathJS identifier character,
 * unlike `:` which MathJS interprets as a range operator.
 *
 * Example: `årsverkLand_SE`, `forurensningMengde_SO2_luft`
 */
export const KEYED_DP_SEPARATOR = "_"

export type KpiDimension = {
  id: CUID
  slug: string
  name: TranslatedText
  categories: Array<KpiCategory>
}

/**
 * Reference to a question's text input value that should be rendered
 * as an additional row in the report table for this group.
 */
export type AnswerDatapoint = {
  /** Question whose answer to display */
  questionId: CUID
  /** Which text input key to read (e.g. "input_0") */
  inputKey: string
  /** Row label shown in the report table */
  label: TranslatedText
  /** When set, only show this row if the question's primaryAnswer matches */
  showWhen?: {
    primaryAnswer: "true" | "false"
  }
  /** Fallback value used when the question has no active answer (e.g. disabled by a parent question). */
  defaultValue?: string
}

/** Column definition for a repeatable answer table rendered in the report. */
export type RepeatableAnswerColumn = {
  /** Index of the input within each repeatable group */
  inputIndex: number
  /** Column header shown in the table */
  header: TranslatedText
  /** When set, resolve the raw value code through this label registry */
  selectSource?: string
}

/**
 * Renders a repeatable question's answer values as a table in the report.
 * Each repeatable group becomes a row, with columns defined by `columns`.
 */
export type RepeatableAnswerTable = {
  /** Question whose repeatable answer values drive the table rows */
  questionId: CUID
  /** Number of inputs per repeatable group in the source question */
  inputsPerGroup: number
  /** Column definitions mapping input indices to table columns */
  columns: Array<RepeatableAnswerColumn>
}

/**
 * A static label+value pair that is always rendered in the report,
 * regardless of any question or answer state.
 */
export type StaticReportValue = {
  /** Row label shown in the report table */
  label: TranslatedText
  /** The literal value to display */
  value: string
}

export type KpiGroup = {
  name?: TranslatedText
  kpiIds: Array<Kpi["id"]>
  /** Static label+value pairs that always appear in the report */
  staticValues?: Array<StaticReportValue>
  /** Answer values from questions to render as text rows in the report */
  answerDatapoints?: Array<AnswerDatapoint>
  /** Repeatable question answers to render as a table in the report */
  repeatableAnswerTable?: RepeatableAnswerTable
}

export type KpiCategory = {
  id: CUID
  name: TranslatedText
  groups: Array<KpiGroup>
}

export type KpiNumber = `${number}.${number}.${number}` | `${number}.${number}`

type KpiBase = {
  datapoints: Array<string>
  datapointDefaults?: Record<string, number>
  anomalies?: Array<RatingAnomaly["id"]>
  additionalInterpolations?: AdditionalInterpolations[]
  formula?: string
  isFuture?: boolean
  /**
   * Indicates that there is no meaningful calculation to be made until the foundation has been submitted,
   * such as when depending on data extracted from SAF-T files (which is not extracted before this).
   */
  noCalculationUntilSubmitted?: boolean
}

export type LegacyKpi = KpiBase & {
  id: KpiNumber
  name: string
  description: string
  definition: string
  unit: string
  kpiScores?: Array<LegacyKpiMathVariable>
}

export type KpiBreakdownSegment = {
  /** Column header for this segment */
  header: TranslatedText
  /** Key into the shared label-lookup registry (e.g. "countriesNb", "pollutantsNb").
   *  When omitted the raw value is shown as-is. */
  selectSource?: string
  /** When true this segment represents the measurement unit for the value columns.
   *  The report will append the resolved label to each value instead of rendering
   *  a separate column. */
  isUnit?: boolean
}

export type KpiBreakdownValuePrefix = {
  /** Prefix used to find keyed datapoints for this value column */
  datapointPrefix: string
  /** Column header for this value column */
  header: TranslatedText
}

/** A single fallback row displayed when the breakdown has no keyed datapoints
 *  and an optional condition is met (e.g. `ansatteUtenforNorge === 0`). */
export type KpiBreakdownFallbackRow = {
  /** Suffix key parts — one entry per suffix segment.
   *  Example: `["NO"]` → resolved through `countriesNb` → "Norge". */
  suffixValues: string[]
  /** Datapoint names whose values fill each value column — one entry per `valuePrefixes`. */
  valueDatapoints: string[]
}

/** Fallback configuration for a breakdown KPI.
 *  When the normal keyed-datapoint scan yields no rows **and** the optional
 *  condition is satisfied, these static rows are shown instead.
 *
 *  Typical use case: the company has no employees outside Norway, so
 *  `årsverkLand_*` datapoints don't exist, but we still want to show
 *  "Norge: <årsverkTotal>" from A07 data. */
export type KpiBreakdownFallback = {
  /** When provided, fallback rows are only used if `datapoints[condition.datapoint]`
   *  equals `condition.equals`. When omitted, fallback rows are used whenever
   *  the keyed-datapoint scan returns zero rows. */
  condition?: {
    datapoint: string
    equals: number
  }
  /** The rows to display as fallback. */
  rows: KpiBreakdownFallbackRow[]
}

export type KpiBreakdown = {
  /** Describes each _-delimited segment of the datapoint suffix.
   *  For "årsverkLand_SE" there is one segment (country).
   *  For "forurensningMengde_SO2_luft" there are two segments (pollutant, medium). */
  suffixSegments: KpiBreakdownSegment[]
  /** One entry per value column in the breakdown table.
   *  Each entry provides a datapoint prefix and a column header.
   *  All prefixes must share the same suffix key space (same segment structure).
   *  For a single-value breakdown, provide one entry.
   *  Example single: [{ datapointPrefix: "årsverkLand", header: "FTE" }]
   *  Example multi:  [{ datapointPrefix: "hazWasteRecycled", header: "Recycled" },
   *                    { datapointPrefix: "hazWasteDisposed", header: "Disposed" }] */
  valuePrefixes: KpiBreakdownValuePrefix[]
  /** Optional fallback rows displayed when no keyed datapoints are found
   *  and an optional condition is met. See {@link KpiBreakdownFallback}. */
  fallback?: KpiBreakdownFallback
}

/** A unit resolved dynamically from a question answer.
 *  The answer value (e.g. "m2", "kg") is itself a key in the unit registry. */
export type KpiUnitFromQuestion = {
  fromQuestion: CUID
  inputIndex: number
  /** Fallback unit registry key used when the referenced question has no answer (e.g. when the question is disabled). */
  defaultUnit?: string
}

/**
 * How a KPI's measurement unit is defined.
 *
 * - `string` – a key in the unit registry (e.g. "nok", "kg", "bool").
 * - `KpiUnitFromQuestion` – resolved at runtime from a question answer;
 *   the answer code must be a valid unit registry key.
 * - `TranslatedText` – legacy / custom text rendered as-is (backwards compatible).
 */
export type KpiUnit = string | KpiUnitFromQuestion | TranslatedText

export type Kpi = KpiBase & {
  id: CUID
  name: TranslatedText
  description: TranslatedText
  formulaDescription?: TranslatedText
  definition: TranslatedText
  unit: KpiUnit
  kpiNumber: KpiNumber
  businessArea: BusinessArea

  helpText: TranslatedText
  kpiScores?: Array<KpiMathVariable>
  censor?: boolean
  /** If set, this KPI represents a tabular breakdown derived from convention-keyed datapoints */
  breakdown?: KpiBreakdown
}

/** How KPIs are labeled for interpolation in formulae */
export type LegacyKpiMathVariable = `kpi_${number}_${number}_${number}` | `kpi_${number}_${number}`

export type KpiMathVariable = `kpi_${CUID}`
export type KpiEnabledVariable = `${KpiMathVariable}_enabled`
export type KpiBenchmarkVariable = `${KpiMathVariable}_benchmark_${number}`

/** Types of interpolations we only do when specifically requested */
export type AdditionalInterpolations = "kpiBenchmarks"
